import { datadogRum } from '@datadog/browser-rum';
import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import GetAssignedBucket from '|/helpers/experiment/services';

const DL_RECONDITIONING_EXPERIMENT = 'pp-dl-reconditioning-cio-treatment';

interface DlReconditioningContextValue {
  isDlReconditioningTreatment: boolean;
  /**
   * False until the arm is known. A consumer that mounts one of two arms MUST gate on this
   * before mounting either: bucketing is a network call, so the control arm would mount first,
   * its preload effect would create a Persona client, and the flip to treatment would destroy
   * that client and create another. Mirrors CLAUDE.md's note about `useBrand().isSettled` and
   * `review-order.tsx`.
   */
  isSettled: boolean;
}

// Settled control outside the provider: the native webview entry (`index-step-only.tsx`) mounts
// no provider, and an unsettled default would leave a consumer waiting on a gate that never
// resolves.
const DlReconditioningContext = createContext<DlReconditioningContextValue>({
  isDlReconditioningTreatment: false,
  isSettled: true
});

const DlReconditioningProvider: React.FC<
  React.PropsWithChildren<{ isCheckItOutTreatment: boolean | undefined }>
> = ({ isCheckItOutTreatment, children }) => {
  const [isDlReconditioningTreatment, setIsDlReconditioningTreatment] = useState(false);
  const [isSettled, setIsSettled] = useState(false);
  // Which gate value we last decided for — re-decide only if a summary refresh moves it. The
  // sentinel is null rather than undefined so the "not loaded yet" regression test below stays
  // meaningful if the undefined-check on the next line is ever reordered or removed: undefined is
  // inside isCheckItOutTreatment's own domain, so it wouldn't reliably catch that regression.
  const decidedForRef = useRef<boolean | null>(null);

  useEffect(() => {
    // undefined = summary not loaded yet (wait); boolean = loaded. This is also what keeps
    // bucketing out of the pre-auth window: GetAssignedBucket returns 'control' with
    // hasErrors: false when there is no user_id, and the prop derives from a summary that only
    // lands after auth.
    if (isCheckItOutTreatment === undefined || decidedForRef.current === isCheckItOutTreatment) {
      return;
    }
    decidedForRef.current = isCheckItOutTreatment;

    // A summary refresh can drop the customer out of CIO treatment, so clear the previous arm and
    // its RUM tag rather than letting a stale one persist.
    setIsDlReconditioningTreatment(false);
    datadogRum.removeGlobalContextProperty(`experiments.${DL_RECONDITIONING_EXPERIMENT}`);

    // The experiment is nested inside CIO treatment, which is its population definition: a
    // customer outside that population is settled control with no bucketing call and no RUM tag.
    if (!isCheckItOutTreatment) {
      setIsSettled(true);
      return;
    }

    setIsSettled(false);
    const decidedFor = isCheckItOutTreatment;
    GetAssignedBucket({ experimentName: DL_RECONDITIONING_EXPERIMENT, cacheMinutes: 15 })
      .then(result => {
        // A superseding gate flip invalidates this in-flight lookup.
        if (decidedForRef.current !== decidedFor) return;
        // An errored lookup collapses to control by decision: at 0% every lookup returns control
        // anyway, so the RUM cohort is tagged control too rather than left untagged to keep the
        // cohort clean. The stricter "errored ≠ unbucketed" variant is a precondition for ramping
        // off 0%, not something built here.
        const bucket = result.assignedBucket ?? 'control';
        setIsDlReconditioningTreatment(bucket === 'treatment');
        // Server-sourced experiment — not in ExperimentProvider, requires manual RUM context
        datadogRum.setGlobalContextProperty(`experiments.${DL_RECONDITIONING_EXPERIMENT}`, bucket);
        setIsSettled(true);
      })
      .catch(() => {
        // GetAssignedBucket wraps its own request errors into a resolved { hasErrors: true }
        // result, so this should be unreachable — but without it, a rejection here would wedge
        // isSettled at false forever, hanging any consumer gated on it. Matches use-lazy-bucket.ts.
        if (decidedForRef.current !== decidedFor) return;
        setIsDlReconditioningTreatment(false);
        setIsSettled(true);
      });
  }, [isCheckItOutTreatment]);

  const value = useMemo(
    () => ({ isDlReconditioningTreatment, isSettled }),
    [isDlReconditioningTreatment, isSettled]
  );

  return (
    <DlReconditioningContext.Provider value={value}>{children}</DlReconditioningContext.Provider>
  );
};

export default DlReconditioningContext;
export { DlReconditioningProvider };
